import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  getIssues,
  getOpenIssues,
  addIssue,
  updateIssue,
  deleteIssue,
  resolveIssue,
  deleteIssueAttachment,
  downloadIssueAttachment,
  getIssueAttachmentFile,
  getUsers,
  type ExpenseCostSplit,
} from '../../api';
import { Issue, IssueAttachment, User } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import { useAircraft } from '../../aircraft/AircraftContext';
import { useFeedback } from '../../hooks/useFeedback';
import { useLoadMore } from '../../hooks/useLoadMore';
import { DateFormat } from '../../utils/DateFormat';
import { getApiErrorMessage } from '../../utils/apiError';
import {
  formatRub,
  issueCostSplitLabel,
  issueDateLabel,
  issueStatusLabel,
  severityLabel,
} from '../../utils/issueDisplay';
import {
  SortConfig,
  SortValueKind,
  defaultDirectionForKind,
  sortByConfig,
  toggleSortConfig,
} from '../../utils/tableSort';
import { downloadIssuesCsv } from '../../utils/exportIssuesCsv';
import { uploadIssueFiles } from '../../utils/issueFiles';
import {
  blobWithMime,
  previewKindFromName,
  type PreviewKind,
} from '../../utils/filePreview';
import PageBackLink from '../../components/PageBackLink/PageBackLink';
import AddIssueModal from '../../components/AddIssueModal/AddIssueModal';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import DocumentPreviewModal from '../../components/DocumentPreviewModal/DocumentPreviewModal';
import EditIssueModal from '../../components/EditIssueModal/EditIssueModal';
import IssueAttachmentsBlock from '../../components/IssueAttachmentsBlock/IssueAttachmentsBlock';
import RecordDetailsModal from '../../components/RecordDetailsModal/RecordDetailsModal';
import ResolveIssueModal from '../../components/ResolveIssueModal/ResolveIssueModal';
import LoadMoreButton from '../../components/LoadMoreButton/LoadMoreButton';
import PilotBadge from '../../components/PilotBadge/PilotBadge';
import SortableTh from '../../components/SortableTh/SortableTh';
import '../FlightsHistoryPage/FlightsHistoryPage.css';
import './IssuesPage.css';

type IssueSortKey = keyof Issue | 'flight_date';

const issueSortKind = (key: IssueSortKey): SortValueKind => {
  switch (key) {
    case 'severity':
      return 'severity';
    case 'is_resolved':
      return 'boolean';
    case 'flight_date':
    case 'issue_date':
    case 'created_at':
    case 'resolved_at':
      return 'date';
    case 'cost':
    case 'id':
    case 'flight_id':
      return 'number';
    default:
      return 'string';
  }
};

const issueSortValue = (row: Issue, key: IssueSortKey): unknown =>
  key === 'flight_date' ? row.flight_date : row[key as keyof Issue];

const IssuesPage = () => {
  const [issues, setIssues] = useState<Issue[]>([]);
  const [openCount, setOpenCount] = useState<number>(0);
  const [members, setMembers] = useState<User[]>([]);
  const [adding, setAdding] = useState(false);
  const [resolvingIssue, setResolvingIssue] = useState<Issue | null>(null);
  const [viewingIssue, setViewingIssue] = useState<Issue | null>(null);
  const [editing, setEditing] = useState<Issue | null>(null);
  const [deleting, setDeleting] = useState<Issue | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [filesUploading, setFilesUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState<IssueAttachment | null>(null);
  const [previewFiles, setPreviewFiles] = useState<IssueAttachment[]>([]);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewKind, setPreviewKind] = useState<PreviewKind>('other');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const previewRequest = useRef(0);

  const { canMaintain, canReportIssues, isAdmin, currentUserId } = useAuth();
  const { aircraftId, current, dataRevision } = useAircraft();
  const { showError, showSuccess, feedbackNode } = useFeedback();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightFlightId = Number(searchParams.get('flight_id')) || null;
  const highlightRef = useRef<HTMLTableRowElement | null>(null);

  // По умолчанию: открытые выше закрытых, внутри — high → medium → low
  const [sortConfig, setSortConfig] = useState<SortConfig<IssueSortKey>>({
    key: 'is_resolved',
    direction: 'asc',
  });

  const firstHighlightIssueId = useMemo(() => {
    if (highlightFlightId == null) return null;
    return issues.find((i) => i.flight_id === highlightFlightId)?.id ?? null;
  }, [issues, highlightFlightId]);

  const loadData = async () => {
    try {
      const [allRes, openRes] = await Promise.all([getIssues(), getOpenIssues()]);
      setIssues(allRes.data);
      setOpenCount(openRes.data.length);
      setViewingIssue((current) => {
        if (!current) return null;
        return allRes.data.find((item) => item.id === current.id) || current;
      });
      setEditing((current) => {
        if (!current) return null;
        return allRes.data.find((item) => item.id === current.id) || current;
      });
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка загрузки неисправностей'));
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    const run = async () => {
      try {
        const [allRes, openRes] = await Promise.all([
          getIssues({ signal: controller.signal }),
          getOpenIssues({ signal: controller.signal }),
        ]);
        setIssues(allRes.data);
        setOpenCount(openRes.data.length);
      } catch (error) {
        if (controller.signal.aborted) return;
        showError(getApiErrorMessage(error, 'Ошибка загрузки неисправностей'));
      }
    };
    void run();
    return () => controller.abort();
  }, [aircraftId, dataRevision, showError]);

  useEffect(() => {
    let cancelled = false;
    void getUsers()
      .then((res) => {
        if (!cancelled) setMembers(res.data || []);
      })
      .catch(() => {
        if (!cancelled) setMembers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [aircraftId]);

  const clearHighlight = () => {
    if (!highlightFlightId) return;
    searchParams.delete('flight_id');
    setSearchParams(searchParams, { replace: true });
  };

  const handleAdd = async (data: {
    description: string;
    severity: string;
    issue_date: string;
    comment: string;
    files: File[];
  }) => {
    try {
      const created = await addIssue({
        user_id: currentUserId,
        description: data.description,
        severity: data.severity,
        issue_date: data.issue_date,
        comment: data.comment,
      });
      if (data.files.length > 0 && created.data.id) {
        try {
          await uploadIssueFiles(created.data.id, data.files);
        } catch (error) {
          setAdding(false);
          await loadData();
          showError(
            getApiErrorMessage(
              error,
              'Неисправность добавлена, но файлы загрузить не удалось'
            )
          );
          return;
        }
      }
      setAdding(false);
      await loadData();
      showSuccess('Неисправность добавлена');
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка добавления неисправности'));
    }
  };

  const canDeleteIssue = (issue: Issue) =>
    canReportIssues &&
    (isAdmin || (issue.user_id != null && issue.user_id === currentUserId));

  const canEditIssue = (issue: Issue) => canDeleteIssue(issue);

  const closePreview = () => {
    previewRequest.current += 1;
    setPreviewFile(null);
    setPreviewFiles([]);
    setPreviewLoading(false);
    setPreviewError(null);
    setPreviewKind('other');
    setPreviewUrl(null);
  };

  const handlePreview = async (
    file: IssueAttachment,
    files: IssueAttachment[] = [file]
  ) => {
    setPreviewFiles(files);
    const requestId = previewRequest.current + 1;
    previewRequest.current = requestId;
    const name = file.original_name || file.filename;
    const kind = previewKindFromName(name);
    setPreviewUrl(null);
    setPreviewFile(file);
    setPreviewKind(kind);
    setPreviewError(null);
    if (kind === 'other') {
      setPreviewLoading(false);
      return;
    }
    setPreviewLoading(true);
    try {
      const res = await getIssueAttachmentFile(file.issue_id, file.id);
      if (previewRequest.current !== requestId) return;
      const blob = blobWithMime(res.data, name);
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (error) {
      if (previewRequest.current !== requestId) return;
      setPreviewError(getApiErrorMessage(error, 'Не удалось открыть файл'));
    }
    if (previewRequest.current === requestId) {
      setPreviewLoading(false);
    }
  };

  const previewIndex = previewFile
    ? previewFiles.findIndex((item) => item.id === previewFile.id)
    : -1;

  const goPreview = (index: number) => {
    const file = previewFiles[index];
    if (!file) return;
    void handlePreview(file, previewFiles);
  };

  const handleDownloadAttachment = async (file: IssueAttachment) => {
    try {
      await downloadIssueAttachment(
        file.issue_id,
        file.id,
        file.original_name || file.filename
      );
    } catch (error) {
      showError(getApiErrorMessage(error, 'Не удалось скачать файл'));
    }
  };

  const handleDeleteAttachment = async (file: IssueAttachment) => {
    if (!window.confirm(`Удалить «${file.original_name || file.filename}»?`)) {
      return;
    }
    try {
      await deleteIssueAttachment(file.issue_id, file.id);
      if (previewFile?.id === file.id) closePreview();
      await loadData();
      showSuccess('Файл удалён');
    } catch (error) {
      showError(getApiErrorMessage(error, 'Не удалось удалить файл'));
    }
  };

  const handleAddAttachments = async (files: File[]) => {
    if (!editing || files.length === 0) return;
    setFilesUploading(true);
    try {
      await uploadIssueFiles(editing.id, files);
      await loadData();
      showSuccess(
        files.length === 1 ? 'Файл загружен' : `Загружено файлов: ${files.length}`
      );
    } catch (error) {
      showError(getApiErrorMessage(error, 'Не удалось загрузить файл'));
    }
    setFilesUploading(false);
  };

  const handleUpdate = async (data: {
    description: string;
    severity: string;
    issue_date: string;
    resolved_date?: string;
    resolved_by?: string;
    cost?: number;
    cost_split?: ExpenseCostSplit;
    payer_ids?: number[];
    payer_shares?: number[];
    flight_ids?: number[];
    comment?: string;
  }) => {
    if (!editing) return;
    try {
      await updateIssue(editing.id, data);
      setEditing(null);
      await loadData();
      showSuccess('Неисправность изменена');
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка изменения неисправности'));
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      await deleteIssue(deleting.id);
      setDeleting(null);
      await loadData();
      showSuccess('Неисправность удалена');
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка удаления неисправности'));
    }
    setDeleteBusy(false);
  };

  const handleResolveConfirm = async (data: {
    resolved_date: string;
    resolved_by: string;
    cost: number;
    cost_split: ExpenseCostSplit;
    payer_ids?: number[];
    payer_shares?: number[];
    flight_ids?: number[];
  }) => {
    if (!resolvingIssue) return;
    try {
      await resolveIssue(resolvingIssue.id, data);
      setResolvingIssue(null);
      await loadData();
      showSuccess('Неисправность устранена');
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка закрытия неисправности'));
    }
  };

  const handleSort = (key: IssueSortKey) => {
    setSortConfig((prev) =>
      toggleSortConfig(prev, key, defaultDirectionForKind(issueSortKind(key)))
    );
  };

  const sortedIssues = useMemo(() => {
    const secondary: SortConfig<IssueSortKey>[] = [];
    if (sortConfig.key !== 'is_resolved') {
      secondary.push({ key: 'is_resolved', direction: 'asc' });
    }
    if (sortConfig.key !== 'severity') {
      secondary.push({ key: 'severity', direction: 'desc' });
    }
    if (sortConfig.key !== 'issue_date' && sortConfig.key !== 'created_at') {
      secondary.push({ key: 'issue_date', direction: 'desc' });
    }
    return sortByConfig(
      issues,
      sortConfig,
      issueSortValue,
      issueSortKind,
      secondary
    );
  }, [issues, sortConfig]);

  const highlightIndex = useMemo(() => {
    if (firstHighlightIssueId == null) return null;
    const idx = sortedIssues.findIndex((i) => i.id === firstHighlightIssueId);
    return idx >= 0 ? idx : null;
  }, [sortedIssues, firstHighlightIssueId]);

  const { visibleCount, hasMore, remaining, nextBatch, loadMore, loadAll } = useLoadMore(
    sortedIssues.length,
    {
      resetKey: `${sortConfig.key}-${sortConfig.direction}`,
      ensureIndex: highlightIndex,
    }
  );
  const visibleIssues = sortedIssues.slice(0, visibleCount);

  const handleExport = () => {
    if (sortedIssues.length === 0) {
      showError('Нет неисправностей для выгрузки');
      return;
    }
    const tail = current?.tail_number?.trim() || `aircraft-${aircraftId}`;
    downloadIssuesCsv(sortedIssues, `issues-${tail}`);
    showSuccess('CSV скачан');
  };

  useEffect(() => {
    if (!highlightFlightId || issues.length === 0) return;
    highlightRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    const found = issues.find((item) => item.flight_id === highlightFlightId);
    if (found) setViewingIssue(found);
  }, [highlightFlightId, issues, visibleCount]);

  const closeViewing = () => {
    setViewingIssue(null);
    clearHighlight();
  };

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  return (
    <div className="issues-page" onClick={clearHighlight}>
      {feedbackNode}
      <div className="issues-header">
        <PageBackLink to="/" />
        <h2>Неисправности</h2>
      </div>

      <div className={`open-counter ${openCount > 0 ? 'warning' : 'ok'}`}>
        <strong>Текущих неисправностей: {openCount}</strong>
      </div>

      {canReportIssues ? (
        <button
          type="button"
          className="page-cta-btn"
          onClick={() => setAdding(true)}
        >
          Добавить неисправность
        </button>
      ) : null}

      <div className="issues-export-row">
        <button
          type="button"
          className="btn-export-issues"
          onClick={handleExport}
          disabled={sortedIssues.length === 0}
        >
          Скачать CSV
        </button>
      </div>

      <div className="flights-history-table-wrap">
        <table className="flights-history-table flights-history-table-compact">
          <thead>
            <tr>
              <SortableTh
                label="Дата"
                columnKey="issue_date"
                sortConfig={sortConfig}
                onSort={handleSort}
              />
              <SortableTh
                label="Описание"
                columnKey="description"
                sortConfig={sortConfig}
                onSort={handleSort}
              />
              <SortableTh
                label="Важность"
                columnKey="severity"
                sortConfig={sortConfig}
                onSort={handleSort}
              />
            </tr>
          </thead>
          <tbody>
            {visibleIssues.length === 0 ? (
              <tr>
                <td
                  colSpan={3}
                  style={{ textAlign: 'center', padding: '30px', color: '#7f8c8d' }}
                >
                  Нет неисправностей
                </td>
              </tr>
            ) : (
              visibleIssues.map((i) => {
                const isHighlighted =
                  highlightFlightId != null && i.flight_id === highlightFlightId;
                return (
                  <tr
                    key={i.id}
                    ref={i.id === firstHighlightIssueId ? highlightRef : null}
                    className={[
                      'flights-history-row',
                      i.is_resolved ? 'resolved' : 'unresolved',
                      isHighlighted ? 'row-highlight' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={(e) => {
                      e.stopPropagation();
                      setViewingIssue(i);
                    }}
                  >
                    <td>{issueDateLabel(i)}</td>
                    <td className="record-row-main">{i.description}</td>
                    <td>
                      <span
                        className={[
                          'severity-badge',
                          i.severity === 'low'
                            ? 'severity-low'
                            : i.severity === 'medium'
                              ? 'severity-medium'
                              : 'severity-high',
                        ].join(' ')}
                      >
                        {severityLabel(i.severity)}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {hasMore ? (
        <LoadMoreButton
          remaining={remaining}
          nextBatch={nextBatch}
          onClick={loadMore}
          onShowAll={loadAll}
        />
      ) : null}

      {adding ? (
        <AddIssueModal
          onClose={() => setAdding(false)}
          onConfirm={(data) => void handleAdd(data)}
        />
      ) : null}

      {viewingIssue ? (
        <RecordDetailsModal
          title="Неисправность"
          summary={
            <>
              <span className="record-details-date">
                {issueDateLabel(viewingIssue)}
              </span>
              <span className="record-details-lead">
                {viewingIssue.description}
              </span>
            </>
          }
          rows={[
            {
              label: 'Автор',
              value: (
                <PilotBadge
                  name={viewingIssue.author}
                  color={viewingIssue.author_color}
                />
              ),
            },
            {
              label: 'Важность',
              value: (
                <strong>
                  <span
                    className={[
                      'severity-badge',
                      viewingIssue.severity === 'low'
                        ? 'severity-low'
                        : viewingIssue.severity === 'medium'
                          ? 'severity-medium'
                          : 'severity-high',
                    ].join(' ')}
                  >
                    {severityLabel(viewingIssue.severity)}
                  </span>
                </strong>
              ),
            },
            { label: 'Статус', value: issueStatusLabel(viewingIssue) },
            {
              label: 'Полёт',
              value:
                viewingIssue.flight_id && viewingIssue.flight_date ? (
                  <button
                    type="button"
                    className="table-link"
                    onClick={() =>
                      navigate(`/stats?flight_id=${viewingIssue.flight_id}`)
                    }
                  >
                    {DateFormat(viewingIssue.flight_date)}
                  </button>
                ) : (
                  '—'
                ),
            },
            ...(viewingIssue.is_resolved
              ? [
                  {
                    label: 'Дата устранения',
                    value: viewingIssue.resolved_at
                      ? DateFormat(String(viewingIssue.resolved_at).slice(0, 10))
                      : '—',
                  },
                  {
                    label: 'Кем устранена',
                    value: viewingIssue.resolved_by || '—',
                  },
                  {
                    label: 'Стоимость',
                    value: formatRub(viewingIssue.cost),
                  },
                  {
                    label: 'Тип расхода',
                    value: issueCostSplitLabel(
                      viewingIssue.cost_split,
                      members,
                      viewingIssue.payer_ids || []
                    ),
                  },
                ]
              : []),
          ]}
          note={viewingIssue.comment}
          extra={
            <IssueAttachmentsBlock
              files={viewingIssue.attachments || []}
              onOpen={(file, files) => void handlePreview(file, files)}
            />
          }
          actions={[
            ...(canEditIssue(viewingIssue)
              ? [
                  {
                    label: 'Изменить',
                    onClick: () => {
                      setEditing(viewingIssue);
                      closeViewing();
                    },
                  },
                ]
              : []),
            ...(!viewingIssue.is_resolved && canMaintain
              ? [
                  {
                    label: 'Устранить',
                    onClick: () => {
                      setResolvingIssue(viewingIssue);
                      closeViewing();
                    },
                  },
                ]
              : []),
            ...(canDeleteIssue(viewingIssue)
              ? [
                  {
                    label: 'Удалить',
                    onClick: () => {
                      setDeleting(viewingIssue);
                      closeViewing();
                    },
                    danger: true,
                  },
                ]
              : []),
          ]}
          onClose={closeViewing}
        />
      ) : null}

      {previewFile ? (
        <DocumentPreviewModal
          title={previewFile.original_name || previewFile.filename}
          objectUrl={previewUrl}
          kind={previewKind}
          loading={previewLoading}
          error={previewError}
          fileIndex={Math.max(0, previewIndex)}
          fileCount={previewFiles.length}
          onPrev={() => goPreview(previewIndex - 1)}
          onNext={() => goPreview(previewIndex + 1)}
          onDownload={() => void handleDownloadAttachment(previewFile)}
          onClose={closePreview}
        />
      ) : null}

      {resolvingIssue && (
        <ResolveIssueModal
          aircraft={current}
          onClose={() => setResolvingIssue(null)}
          onConfirm={handleResolveConfirm}
        />
      )}

      {editing ? (
        <EditIssueModal
          issue={editing}
          aircraft={current}
          canEditResolution={canMaintain}
          filesUploading={filesUploading}
          onClose={() => setEditing(null)}
          onConfirm={(data) => void handleUpdate(data)}
          onOpenFile={(file, files) => void handlePreview(file, files)}
          onDeleteFile={(file) => void handleDeleteAttachment(file)}
          onAddFiles={(files) => void handleAddAttachments(files)}
        />
      ) : null}

      {deleting ? (
        <ConfirmModal
          title="Удалить неисправность?"
          message={
            deleting.is_resolved
              ? `${deleting.description}${deleting.issue_date ? ` от ${DateFormat(deleting.issue_date)}` : ''}. Вместе с ней удалится связанный расход.`
              : `${deleting.description}${deleting.issue_date ? ` от ${DateFormat(deleting.issue_date)}` : ''}.`
          }
          confirmLabel="Удалить"
          danger
          submitting={deleteBusy}
          onCancel={() => {
            if (!deleteBusy) setDeleting(null);
          }}
          onConfirm={() => void handleDeleteConfirm()}
        />
      ) : null}
    </div>
  );
};

export default IssuesPage;